import type { WindField } from "@divespots/contracts";
import { createWindSampler } from "@divespots/contracts";
import type { Map as LeafletMap } from "leaflet";
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { L } from "../leaflet-global.ts";
import {
  buildScreenField,
  canvasDpr,
  createParticles,
  lookup,
  particleCount,
  stepParticles,
} from "./particles.ts";
import type { Particles, PixelSampler, ScreenField } from "./particles.ts";
import { ARROW_HALO_PX, ARROW_LENGTH_PX, ARROW_WEIGHT_PX, windClass, windVar } from "./ramp.ts";
import type { WindClass } from "./ramp.ts";
import { useReducedMotion } from "./useReducedMotion.ts";

export type WindMode = "particles" | "arrows";

/** Between the tiles (200) and the overlay/marker panes (400+): pins and clusters stay on top. */
const PANE = "wind";
const PANE_Z = 350;
/** Wind lookup grid for the particles, in CSS px. */
const FIELD_CELL = 12;
/** Arrow grid spacing for reduced motion, in CSS px. */
const ARROW_GRID = 64;
/** Trail memory: the canvas fades with this time constant (seconds). */
const FADE_TAU_S = 0.4;
const MAX_FRAME_S = 0.05;
const PHONE_QUERY = "(max-width: 899.98px)";

const token = (name: string, fallback: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

/**
 * Canvas layer of the wind overlay: animated particles with fading trails, or (reduced motion) a
 * static arrow grid. It only draws; the maths are in particles.ts and the contracts sampler.
 */
export class WindCanvasLayer extends L.Layer {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private mapRef: LeafletMap | null = null;
  private sampler: ReturnType<typeof createWindSampler> | null = null;
  /** Forecast time; null = no wind at the step on show (nothing is drawn, the loop stays). */
  private tMs: number | null = null;
  private modeValue: WindMode = "particles";
  private aerial = false;
  private size = { w: 0, h: 0 };
  private screen: ScreenField | null = null;
  private dirty = true;
  private particles: Particles | null = null;
  private moving = false;
  private raf = 0;
  private last = 0;
  private trail = "rgba(16, 42, 67, 0.72)";

  get particleCount(): number {
    return this.modeValue === "particles" && this.tMs !== null ? (this.particles?.n ?? 0) : 0;
  }
  get time(): number | null {
    return this.tMs;
  }
  get mode(): WindMode {
    return this.modeValue;
  }

  override onAdd(map: LeafletMap): this {
    this.mapRef = map;
    const pane = map.getPane(PANE) ?? map.createPane(PANE);
    pane.style.zIndex = String(PANE_Z);
    pane.style.pointerEvents = "none";
    const canvas = document.createElement("canvas");
    canvas.className = "wind-canvas leaflet-zoom-hide";
    canvas.setAttribute("aria-hidden", "true");
    pane.appendChild(canvas);
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    map.on("movestart zoomstart", this.pause, this);
    map.on("moveend resize", this.reset, this);
    document.addEventListener("visibilitychange", this.visibility);
    map.whenReady(this.reset, this);
    return this;
  }

  override onRemove(map: LeafletMap): this {
    this.stop();
    map.off("movestart zoomstart", this.pause, this);
    map.off("moveend resize", this.reset, this);
    document.removeEventListener("visibilitychange", this.visibility);
    this.canvas?.remove();
    this.canvas = null;
    this.ctx = null;
    this.particles = null;
    this.screen = null;
    this.mapRef = null;
    return this;
  }

  setField(field: Pick<WindField, "grid" | "times" | "u" | "v">): void {
    this.sampler = createWindSampler(field);
    this.dirty = true;
    this.reset();
  }

  /**
   * A new time never resets the particles: they pick it up on the next frame. `null` = no wind at
   * this step: the canvas is cleared and stays blank, the loop keeps running, and the particles
   * flow again as soon as a time with wind comes back.
   */
  setTime(tMs: number | null): void {
    if (tMs === this.tMs) return;
    this.tMs = tMs;
    this.dirty = true;
    if (tMs === null) this.clearCanvas();
    // the static arrows redraw now
    if (this.modeValue === "arrows") this.reset();
  }

  setMode(mode: WindMode): void {
    if (mode === this.modeValue) return;
    this.modeValue = mode;
    this.reset();
  }

  setAerial(aerial: boolean): void {
    this.aerial = aerial;
    this.trail = token(aerial ? "--wind-trail-aerial" : "--wind-trail", this.trail);
  }

  private visibility = (): void => {
    if (document.hidden) this.stop();
    else this.reset();
  };

  private pause = (): void => {
    this.moving = true;
    this.stop();
  };

  private stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** (Re)sizes the canvas to the viewport, rebuilds the screen-space field and restarts. */
  private reset = (): void => {
    const map = this.mapRef;
    const canvas = this.canvas;
    const ctx = this.ctx;
    if (!map || !canvas || !ctx || !this.sampler) return;
    this.stop();
    this.moving = false;
    const s = map.getSize();
    if (s.x === 0 || s.y === 0) return;
    const dpr = canvasDpr(window.devicePixelRatio);
    canvas.width = Math.round(s.x * dpr);
    canvas.height = Math.round(s.y * dpr);
    canvas.style.width = `${s.x}px`;
    canvas.style.height = `${s.y}px`;
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, s.x, s.y);
    this.size = { w: s.x, h: s.y };
    this.trail = token(this.aerial ? "--wind-trail-aerial" : "--wind-trail", this.trail);
    this.buildField();
    if (this.modeValue === "arrows") {
      this.particles = null;
      this.drawArrows();
      return;
    }
    const phone = window.matchMedia?.(PHONE_QUERY).matches === true;
    this.particles = createParticles(particleCount(s.x, s.y, phone), s.x, s.y, Math.random);
    if (!document.hidden) this.start();
  };

  private clearCanvas(): void {
    const { w, h } = this.size;
    this.ctx?.clearRect(0, 0, w, h);
  }

  private pixelSampler(): PixelSampler {
    const map = this.mapRef!;
    const sampler = this.sampler!;
    const t = this.tMs;
    if (t === null) return () => null;
    return (x, y) => {
      const ll = map.containerPointToLatLng([x, y]);
      return sampler(ll.lng, ll.lat, t);
    };
  }

  private buildField(): void {
    this.screen = buildScreenField(this.size.w, this.size.h, FIELD_CELL, this.pixelSampler());
    this.dirty = false;
  }

  private start(): void {
    if (this.raf || this.moving) return;
    this.last = 0;
    this.raf = requestAnimationFrame(this.frame);
  }

  private frame = (now: number): void => {
    this.raf = 0;
    const ctx = this.ctx;
    const p = this.particles;
    if (!ctx || !p || this.moving || document.hidden) return;
    const dt = this.last ? Math.min((now - this.last) / 1000, MAX_FRAME_S) : 1 / 60;
    this.last = now;
    if (this.tMs === null) {
      // no wind at this step: nothing moves or is drawn, but the loop stays so it resumes at once
      this.raf = requestAnimationFrame(this.frame);
      return;
    }
    if (this.dirty) this.buildField();
    const { w, h } = this.size;
    stepParticles(p, dt, w, h, this.screen!, Math.random);

    ctx.globalCompositeOperation = "destination-out";
    ctx.fillStyle = `rgba(0, 0, 0, ${1 - Math.exp(-dt / FADE_TAU_S)})`;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = this.trail;
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    for (let i = 0; i < p.n; i++) {
      if (!p.live[i]) continue;
      ctx.moveTo(p.px[i]!, p.py[i]!);
      ctx.lineTo(p.x[i]!, p.y[i]!);
    }
    ctx.stroke();
    this.raf = requestAnimationFrame(this.frame);
  };

  /** Reduced motion: one arrow per grid point, coloured by speed class, nothing where there is no data. */
  private drawArrows(): void {
    const ctx = this.ctx;
    if (!ctx || !this.screen) return;
    const { w, h } = this.size;
    ctx.clearRect(0, 0, w, h);
    if (this.tMs === null) return;
    const colors = ([0, 1, 2, 3] as const).map((c) => token(windVar(c), "#3f7fa6"));
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (let y = ARROW_GRID / 2; y < h; y += ARROW_GRID)
      for (let x = ARROW_GRID / 2; x < w; x += ARROW_GRID) {
        const wind = lookup(this.screen, x, y);
        if (!wind) continue;
        const speed = Math.hypot(wind.u, wind.v);
        // the arrow points where the air moves to; screen y runs down
        const a = Math.atan2(-wind.v, wind.u);
        const c = windClass(speed) as WindClass;
        drawArrow(ctx, x, y, a, c, colors[c]!, this.trail);
      }
  }
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  band: WindClass,
  color: string,
  halo: string,
): void {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const half = ARROW_LENGTH_PX[band] / 2;
  const head = 4 + band;
  const tx = x + c * half;
  const ty = y + s * half;
  const path = () => {
    ctx.beginPath();
    ctx.moveTo(x - c * half, y - s * half);
    ctx.lineTo(tx, ty);
    for (const sign of [-1, 1]) {
      ctx.moveTo(tx, ty);
      ctx.lineTo(tx - c * head * 1.5 + sign * s * head, ty - s * head * 1.5 - sign * c * head);
    }
    ctx.stroke();
  };
  // halo first (the map-side trail colour), then the ramp colour on top
  ctx.strokeStyle = halo;
  ctx.lineWidth = ARROW_WEIGHT_PX[band] + 2 * ARROW_HALO_PX;
  path();
  ctx.strokeStyle = color;
  ctx.lineWidth = ARROW_WEIGHT_PX[band];
  path();
}

interface Props {
  field: WindField;
  /** Forecast time in epoch ms (the layer interpolates between the hours); null = no wind at this step. */
  t: number | null;
  aerial: boolean;
}

/** react-leaflet child of the overview map: mounted only while the overlay is on. */
export function WindLayer({ field, t, aerial }: Props) {
  const map = useMap();
  const layer = useRef<WindCanvasLayer | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const l = new WindCanvasLayer();
    layer.current = l;
    l.addTo(map);
    // E2E builds only: the tests read what the overlay is doing from here
    if (import.meta.env.VITE_E2E === "1") {
      Object.defineProperty(window, "__wind", {
        configurable: true,
        get: () => ({ particles: l.particleCount, t: l.time, mode: l.mode }),
      });
    }
    return () => {
      l.remove();
      layer.current = null;
      if (import.meta.env.VITE_E2E === "1") delete (window as { __wind?: unknown }).__wind;
    };
  }, [map]);

  // order matters: mode and colour first, then the field (whose reset starts the animation)
  useEffect(() => layer.current?.setMode(reduced ? "arrows" : "particles"), [reduced]);
  useEffect(() => layer.current?.setAerial(aerial), [aerial]);
  useEffect(() => layer.current?.setTime(t), [t]);
  useEffect(() => layer.current?.setField(field), [field]);
  return null;
}
